/**
 * Footnote/endnote body editing (see PageSetupBtn.tsx for why these are popovers rather than side
 * panels). `CanonicalDocument.Footnotes`/
 * `.Endnotes` are document-wide lists, NOT part of the ProseMirror document — they're threaded
 * through TemplateEditorPage.tsx's own `editorDocMeta` state alongside the main editor instance
 * (see fromCdm.ts's header comment) — so, unlike every other toolbar button here, this one needs
 * props from its parent for that state rather than reading everything off `editor` alone.
 *
 * Same mini-editor technique as HeaderFooterBtn.tsx: a `Footnote`/`Endnote`'s `content` is a
 * `DocBlock[]`, identical in shape to a `HeaderFooter`'s, so this reuses `cdmExtensions` and
 * `blocksToPm`/`pmToBlocks` the same way, wrapping in a synthetic `section` on load and
 * unwrapping on save. One difference: there can be *many* footnotes/endnotes, not one fixed slot,
 * so this shows a picker (by id) plus an "insert new" action that both creates a fresh entry AND
 * inserts the `footnoteRef`/`endnoteRef` atom at the cursor in the main document — the two are
 * only meaningful together (a note with no reference is invisible; a reference to a missing note
 * renders as a bare bracketed id, see schema.ts's `FootnoteRef`/`EndnoteRef`).
 *
 * Also gets a compact Bold/Italic/Underline row (`MiniFormatToolbar.tsx`, shared with
 * `HeaderFooterBtn.tsx`'s mini editors) above the note body — see that component's own header
 * comment for why it's this narrow a set rather than the main toolbar's fuller one.
 */
import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSuperscript as faNoteIcon, faPlus } from '@fortawesome/free-solid-svg-icons'
import { useEditor, EditorContent, type JSONContent } from '@tiptap/react'
import type { useEditor as useEditorType } from '@tiptap/react'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import { PopoverBtn } from './ToolbarControls'
import { MiniFormatToolbar } from './MiniFormatToolbar'
import { cdmExtensions } from '../../cdm/schema'
import { blocksToPm } from '../../cdm/fromCdm'
import { pmToBlocks } from '../../cdm/toCdm'
import { normalizeMonikerKey } from '../../cdm/monikerKey'
import {
  defaultParagraphFormat, defaultPageSetup, defaultRunFormat, defaultSourceLocation,
  type Footnote, type Endnote, type DocBlock,
} from '../../cdm/types'

type CdmEditor = ReturnType<typeof useEditorType>
type NoteKind = 'footnote' | 'endnote'

function contentToDoc(content: DocBlock[] | undefined): JSONContent {
  const pmBlocks = blocksToPm(content ?? [])
  const body = pmBlocks.length > 0 ? pmBlocks : [{ type: 'paragraph', attrs: { format: defaultParagraphFormat() }, content: [] }]
  return {
    type: 'doc',
    content: [{
      type: 'section',
      attrs: { page: defaultPageSetup(), header: null, footer: null, firstPageHeader: null, firstPageFooter: null, evenPageHeader: null, evenPageFooter: null },
      content: body,
    }],
  }
}

function docToContent(doc: JSONContent) {
  return pmToBlocks(doc.content?.[0]?.content ?? [])
}

interface Props {
  mainEditor: CdmEditor
  footnotes: Footnote[]
  endnotes: Endnote[]
  onFootnotesChange: (footnotes: Footnote[]) => void
  onEndnotesChange: (endnotes: Endnote[]) => void
}

export default function FootnoteEndnoteBtn({ mainEditor, footnotes, endnotes, onFootnotesChange, onEndnotesChange }: Props) {
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<NoteKind>('footnote')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [varName, setVarName] = useState('')

  const notes = kind === 'footnote' ? footnotes : endnotes
  const setNotes = kind === 'footnote' ? onFootnotesChange : onEndnotesChange
  const selected = notes.find(n => n.id === selectedId) ?? null

  const noteEditor = useEditor({ extensions: cdmExtensions, content: contentToDoc(undefined) })

  function loadNote(id: number, forKind: NoteKind) {
    const list = forKind === 'footnote' ? footnotes : endnotes
    const note = list.find(n => n.id === id)
    noteEditor?.commands.setContent(contentToDoc(note?.content))
    setSelectedId(id)
  }

  // Returns `current` with the currently-selected note's latest (possibly unsaved-to-React-state)
  // mini-editor content merged in, or `current` untouched if nothing is selected. Both call sites
  // below need this same "flush before I mutate the list" step — applySelected() applies it
  // directly, insertNew() needs the flushed array itself so it can append the new note to the
  // flushed state in one setNotes call rather than two competing ones (see insertNew's own note).
  function flushSelected(current: (Footnote | Endnote)[]): (Footnote | Endnote)[] {
    if (!noteEditor || selectedId == null) return current
    const content = docToContent(noteEditor.getJSON())
    return current.map(n => (n.id === selectedId ? { ...n, content } : n))
  }

  function applySelected() {
    setNotes(flushSelected(notes))
  }

  function insertNew() {
    if (!mainEditor) return
    // Flush the currently-selected note's in-progress edits before creating a new one — found via
    // a component test: clicking "+ New" while a note is selected used to silently discard that
    // note's unsaved typed content, since it reset `noteEditor` to blank without applying first.
    // Computed once here (rather than calling applySelected() then setNotes([...notes, newNote])
    // separately) because two setNotes calls in the same event would race: the second would use
    // the stale `notes` closure and overwrite the first's flush instead of building on it.
    const flushed = flushSelected(notes)
    const nextId = flushed.length > 0 ? Math.max(...flushed.map(n => n.id)) + 1 : 1
    const newNote = { id: nextId, content: [] }
    setNotes([...flushed, newNote])
    mainEditor.chain().focus().insertContent({
      type: kind === 'footnote' ? 'footnoteRef' : 'endnoteRef',
      attrs: kind === 'footnote' ? { footnoteId: nextId } : { endnoteId: nextId },
    }).run()
    noteEditor?.commands.setContent(contentToDoc(undefined))
    setSelectedId(nextId)
  }

  function insertVar() {
    const rawName = varName.trim()
    if (!rawName || !noteEditor) return
    noteEditor.chain().focus().insertContent({
      type: 'placeholder',
      attrs: { rawName, normalizedKey: normalizeMonikerKey(rawName), formatters: null, format: defaultRunFormat(), source: defaultSourceLocation() },
    }).run()
    setVarName('')
  }

  function switchKind(next: NoteKind) {
    applySelected()
    setKind(next)
    setSelectedId(null)
    noteEditor?.commands.setContent(contentToDoc(undefined))
  }

  return (
    <PopoverBtn
      icon={faNoteIcon}
      tooltipText="Footnotes & endnotes"
      active={open}
      onActivate={() => setOpen(true)}
      onDeactivate={() => { applySelected(); setOpen(false) }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, minWidth: 340 }}>
        <div style={{ display: 'flex', borderBottom: `1px solid ${colors.border}` }}>
          <button
            type="button"
            onClick={() => switchKind('footnote')}
            style={{ padding: '4px 12px', border: 'none', borderBottom: `2px solid ${kind === 'footnote' ? colors.primary : 'transparent'}`, background: 'none', fontSize: fontSize.sm, fontWeight: kind === 'footnote' ? 600 : 400, color: kind === 'footnote' ? colors.primary : colors.textSecondary, cursor: 'pointer' }}
          >
            Footnotes
          </button>
          <button
            type="button"
            onClick={() => switchKind('endnote')}
            style={{ padding: '4px 12px', border: 'none', borderBottom: `2px solid ${kind === 'endnote' ? colors.primary : 'transparent'}`, background: 'none', fontSize: fontSize.sm, fontWeight: kind === 'endnote' ? 600 : 400, color: kind === 'endnote' ? colors.primary : colors.textSecondary, cursor: 'pointer' }}
          >
            Endnotes
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
          {notes.map(n => (
            <button
              key={n.id}
              type="button"
              onClick={() => { applySelected(); loadNote(n.id, kind) }}
              style={{ padding: '2px 8px', border: `1px solid ${n.id === selectedId ? colors.primary : colors.border}`, borderRadius: radius.sm, background: n.id === selectedId ? colors.primaryLight : 'none', color: n.id === selectedId ? colors.primary : colors.text, fontSize: fontSize.xs, fontFamily: 'monospace', cursor: 'pointer' }}
            >
              [{n.id}]
            </button>
          ))}
          <button
            type="button"
            onClick={insertNew}
            title={`Insert a new ${kind} reference at the cursor`}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px', border: `1px solid ${colors.primary}`, borderRadius: radius.sm, background: colors.primaryLight, color: colors.primary, fontSize: fontSize.xs, fontWeight: 600, cursor: 'pointer' }}
          >
            <FontAwesomeIcon icon={faPlus} /> New
          </button>
        </div>

        {selected ? (
          <>
            <MiniFormatToolbar editor={noteEditor} />
            <div
              className="cdm-editor"
              style={{ border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: spacing.sm, minHeight: 80, maxHeight: 200, overflow: 'auto', background: colors.surface }}
            >
              <EditorContent editor={noteEditor} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: fontSize.xs, color: colors.textSecondary, fontFamily: 'monospace' }}>{'{{x}}'}</span>
              <input
                type="text"
                value={varName}
                onChange={e => setVarName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); insertVar() } }}
                placeholder="var_name"
                style={{ padding: '3px 6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, width: 110, outline: 'none' }}
              />
              <button type="button" onClick={insertVar} style={{ padding: '3px 8px', border: `1px solid ${colors.primary}`, borderRadius: radius.sm, background: colors.primaryLight, color: colors.primary, fontSize: fontSize.sm, fontWeight: 600, cursor: 'pointer' }}>
                Insert
              </button>
            </div>
          </>
        ) : (
          <div style={{ fontSize: fontSize.sm, color: colors.textMuted, padding: spacing.sm, textAlign: 'center' }}>
            {notes.length > 0 ? `Select a ${kind} above to edit it.` : `No ${kind}s yet — click "New" to insert one at the cursor.`}
          </div>
        )}
      </div>
    </PopoverBtn>
  )
}
